/* eslint-disable react/no-unescaped-entities */
'use client';

import { useEffect, useState } from 'react';

import { useHandlePostulantInvitations } from '../hooks/useHandlePostulantInvitations';

import { Button } from '@/components';
import InputSearch from '@/components/InputSearch';
import { NotFoundResult } from '@/components/NotFoundResult';
import { PaginatorAsync } from '@/components/PaginatorAsync';
import useAuth from '@/hooks/useAuth';
import useTourney from '@/hooks/useTourney';
import { useUsersProfilePic } from '@/hooks/useUsersProfilePic';
import { tournamentUtilService } from '@/services/tournamentUtilService';
import type { UsuarioDTReducidoTO } from '@/types/invites';
import { cn } from '@/utils';
import { TEXTS } from '@/utils/constants';

interface User {
  idUsuario: number;
  nombres: string;
  apellido: string;
  club: string;
  provincia: string;
  nombreEquipo: string;
  fotoPerfil?: string;
  edad: number;
  idEstado: number;
  localidad: string;
  id: number;
  nombreEstado: string;
}

type SearchDTsService = (searchTerm: string, idTorneo: number) => Promise<any>;
type SuggestUsersService = (cantMaxResultados?: any) => Promise<any>;

interface SearchByNameProps {
  idTorneo: number;
  onInvite: (userId: number) => Promise<boolean | undefined>;
  searchService: SearchDTsService;
  suggestUsersService?: SuggestUsersService;
  type: 'tournament' | 'cup' | 'minileagues' | string;
  isDesktop?: boolean;
}

export default function SearchByName({
  idTorneo,
  onInvite,
  searchService,
  suggestUsersService,
  type,
  isDesktop = false,
}: Readonly<SearchByNameProps>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [currentItems, setCurrentItems] = useState<User[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;
  const { userInitialData } = useAuth();
  const { tourneyData } = useTourney();
  const { getStatusValidation } = tournamentUtilService(type, tourneyData);

  // Recommended users state
  const [recommendedUsers, setRecommendedUsers] = useState<
    UsuarioDTReducidoTO[]
  >([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [recommendedError, setRecommendedError] = useState<string | null>(null);
  const { handleInvite, invitedUsers, member, setMember, memberUsers } =
    useHandlePostulantInvitations({
      type,
      idTorneo,
      onInvite,
      tourneyData,
    });

  const { setProfilePic } = useUsersProfilePic();

  // Fetch recommended users on component mount (only in mobile view)
  useEffect(() => {
    if (!isDesktop && suggestUsersService) {
      const fetchRecommendedUsers = async () => {
        try {
          setLoadingRecommended(true);
          setRecommendedError(null);

          const response = await suggestUsersService(100);

          if (response && response.estado === true) {
            if (
              response.usuariosSugeridos &&
              Array.isArray(response.usuariosSugeridos)
            ) {
              setRecommendedUsers(response.usuariosSugeridos);
            } else if (response.usuarios && Array.isArray(response.usuarios)) {
              setRecommendedUsers(response.usuarios);
            } else {
              throw new Error('La respuesta no contiene usuarios');
            }
          } else {
            throw new Error(response.mensaje || 'Error al obtener usuarios');
          }
        } catch (err) {
          setRecommendedError('Error al cargar usuarios recomendados');
          setRecommendedUsers([]);
        } finally {
          setLoadingRecommended(false);
        }
      };

      fetchRecommendedUsers();
    }
  }, [isDesktop, suggestUsersService]);

  useEffect(() => {
    if (isDesktop) {
      const startIndex = currentPage * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setCurrentItems(allUsers.slice(startIndex, endIndex));
    } else {
      setCurrentItems(allUsers);
    }
  }, [allUsers, currentPage, isDesktop]);

  useEffect(() => {
    if (searchQuery.trim().length === 0) {
      setAllUsers([]);
      setCurrentItems([]);
      setError(undefined);
      return;
    }

    if (searchQuery.trim().length < 2) {
      setError(TEXTS.minTwoCharacters);
      return;
    }

    let isCurrentSearch = true;

    const fetchUsers = async () => {
      setLoading(true);

      try {
        const response = await searchService(searchQuery.trim(), idTorneo);
        setMember(false);
        if (!isCurrentSearch) return;

        if ('usuarios' in response && Array.isArray(response.usuarios)) {
          const filteredList = response.usuarios.filter(
            (data: User) => data.idUsuario !== userInitialData?.idUsuarioDT
          );

          if (filteredList.length > 0) {
            setAllUsers(filteredList);
            setCurrentPage(0);
            setError(undefined);
          } else {
            setAllUsers([]);
            setCurrentItems([]);
            setError(TEXTS.notFoundResult);
          }
        } else {
          setAllUsers([]);
          setCurrentItems([]);
          setError(TEXTS.notFoundResult);
        }
      } catch (err) {
        if (!isCurrentSearch) return;
        if (allUsers.length === 0) {
          setError(
            'Ocurrió un error al buscar DTs. Por favor, intenta de nuevo.'
          );
        }
      } finally {
        if (isCurrentSearch) {
          setLoading(false);
        }
      }
    };

    const debounceTimer = setTimeout(fetchUsers, 300);

    return () => {
      clearTimeout(debounceTimer);
      isCurrentSearch = false;
    };
  }, [searchQuery, idTorneo, searchService]);

  const handleCancelSearch = () => {
    setSearchQuery('');
    setAllUsers([]);
    setCurrentItems([]);
    setError(undefined);
  };

  const handlePageChange = (selectedItem: { selected: number }) => {
    const newPage = selectedItem.selected;
    setCurrentPage(newPage);
  };

  const pageCount = Math.ceil(allUsers.length / itemsPerPage);

  const showResults = currentItems.length > 0;
  const showRecommended =
    !isDesktop && searchQuery.trim() === '' && recommendedUsers.length > 0;

  const getStatus = (estado: string, id: number): string => {
    const invitado = 'Invitado';

    if (invitedUsers.has(id)) {
      if (member) {
        return 'Participando';
      } else if (estado !== 'Pendiente Aceptar Dueño') {
        return invitado;
      }
    }

    let status = '';

    switch (estado) {
      case 'Participando':
        status = 'Participando';
        break;
      case 'Pendiente Aceptar Invitado':
        status = invitado;
        break;
      case 'rechazado por el dueño':
        status = invitado;
        break;
      default:
        status = 'Invitar';
        break;
    }

    return status;
  };

  return (
    <div className="space-y-1">
      {type === 'cup' && (
        <p
          className={`${
            !isDesktop
              ? 'text-[#333333] text-base leading-tight mb-6'
              : 'text-md font-medium text-[#333333] mb-6'
          }`}
        >
          Recordá que el equipo suma con los puntos de tu 11 y el de tus 3
          amigos. Si no está completo, no sumará puntos.
        </p>
      )}

      <InputSearch
        handleCancel={handleCancelSearch}
        name="searchQuery"
        placeholder="Buscar por nombre y apellido"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {/* Contenedor con altura mínima fija para evitar saltos en la UI */}
      <div className="min-h-[24px]">
        {loading && (
          <div className="flex justify-center items-center py-1">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#3366ff]" />
          </div>
        )}

        {/* Solo mostramos error si no hay resultados, no está cargando y no se están mostrando recomendados */}
        {error &&
          !loading &&
          !showResults &&
          !showRecommended &&
          error === TEXTS.notFoundResult && <NotFoundResult title={error} />}
        {error &&
          !loading &&
          !showResults &&
          error === TEXTS.minTwoCharacters && (
            <p className="text-red-500 text-xs ">{error}</p>
          )}
      </div>

      {/* Mostramos los usuarios recomendados si estamos en mobile y no hay búsqueda activa */}
      {showRecommended && (
        <div className="mt-4">
          <h2 className="text-[#333333] text-base font-normal mb-4">
            ¿No sabés a quién invitar? Elegí a uno de los DT's recomendados.
          </h2>

          {loadingRecommended ? (
            <div className="flex justify-center items-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3366ff]" />
            </div>
          ) : recommendedError ? (
            <div className="text-red-500 text-sm">{recommendedError}</div>
          ) : (
            <div className="space-y-2">
              {recommendedUsers.map((user) => (
                <div
                  key={user.idUsuario}
                  className="flex items-center justify-between p-2 rounded-md transition-colors"
                >
                  {setProfilePic({
                    alt: user.apellido,
                    dtName:
                      user.nombres.split(' ')[0] +
                      ' ' +
                      user.apellido.split(' ')[0],
                    url: user.fotoPerfil,
                    size: 40,
                  })}
                  <div className="flex-1 ml-3">
                    <p className="font-medium text-[#333333]">{`${user.nombres} ${user.apellido}`}</p>
                    <p className="text-sm text-[#717077]">
                      {user.nombreEquipo}
                    </p>
                    {user.provincia && (
                      <p className="text-xs text-[#717077]">{user.provincia}</p>
                    )}
                  </div>
                  <Button
                    className={cn('w-[115px] h-[36px] px-10 text-xs', {
                      'p-0':
                        (invitedUsers.size > 0 &&
                          invitedUsers.has(user.idUsuario)) ||
                        getStatusValidation(user.idUsuario, memberUsers) !==
                          'Invitar',
                    })}
                    disabled={
                      (invitedUsers.size > 0 &&
                        invitedUsers.has(user.idUsuario)) ||
                      (getStatusValidation(user.idUsuario, memberUsers) !==
                        'Invitar' &&
                        getStatusValidation(user.idUsuario, memberUsers) !==
                          'Esperando Aceptacion')
                    }
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      handleInvite(user.idUsuario);
                    }}
                  >
                    {invitedUsers.size > 0 && invitedUsers.has(user.idUsuario)
                      ? 'Invitado'
                      : getStatusValidation(user.idUsuario, memberUsers)}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Siempre mantenemos este contenedor para los resultados de búsqueda */}
      <div className="space-y-2">
        {showResults &&
          currentItems.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between p-2 rounded-md transition-colors"
            >
              <div>
                {setProfilePic({
                  alt: user.apellido,
                  dtName:
                    user.nombres.split(' ')[0] +
                    ' ' +
                    user.apellido.split(' ')[0],
                  url: user.fotoPerfil,
                  size: 40,
                })}
              </div>
              <div className="flex-1 ml-3">
                <p className="font-medium text-[#333333]">{`${user.nombres} ${user.apellido}`}</p>
                <p className="text-sm text-[#717077]">{user.nombreEquipo}</p>
                {user.localidad && (
                  <p className="text-xs text-[#717077]">
                    {user.localidad}
                    {user.provincia && `, ${user.provincia}`}
                  </p>
                )}
              </div>
              <Button
                className={cn('w-[115px] h-[36px] px-0 text-xs', {
                  'p-0': getStatus(user.nombreEstado, user.id) !== 'Invitar',
                })}
                disabled={getStatus(user.nombreEstado, user.id) !== 'Invitar'}
                type="button"
                variant="secondary"
                onClick={() => handleInvite(user.idUsuario, user.nombreEstado)}
              >
                {getStatus(user.nombreEstado, user.id)}
              </Button>
            </div>
          ))}
      </div>

      {isDesktop && allUsers.length > itemsPerPage && !loading && (
        <PaginatorAsync
          currentPage={currentPage}
          pageCount={pageCount}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
}
